import React, { useState, useEffect, SetStateAction } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClassificationImagesfor,
  fetchImageById,
  fetchImagesNewClass,
  fetchLabelsByProjectPaged,
} from "../../store/slice/ProjectImagesUploadedSlice";
import { AppDispatch, RootState } from "../../store/store";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import { ImageModelProps } from "../../types/interface";
import { VisionStudioSpinner } from "../../components";
import CheckIcon from "@mui/icons-material/Check";
import { AnnotationData } from "./AnnotationClassification";
import {
  deleteAnnotation,
  fetchLabelDetails,
  saveImageAnnotation,
  updateAnnotation,
} from "../../store/slice/projectAnnotationSlice";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

interface ClassificationImagesProps {
  gridCount: number;
  imagesSelection: string;
  setImagesSelection: React.Dispatch<SetStateAction<string>>;
  startStop: boolean;
  setStartStop: React.Dispatch<React.SetStateAction<boolean>>;
  selectedLbl: AnnotationData;
  setSelectedLbl: React.Dispatch<React.SetStateAction<AnnotationData>>;
  taskType: string;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
  showLabels: boolean;
  setShowLabels: React.Dispatch<React.SetStateAction<boolean>>;
  projectId: string;
  setProjectId: React.Dispatch<React.SetStateAction<string>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
  loader: boolean;
  fetchImagesAccordingto: () => Promise<void>;
  annotationImgs: ImageUrlModel[];
  setAnnotationImgs: React.Dispatch<React.SetStateAction<ImageUrlModel[]>>;
  setTotalCount?: React.Dispatch<React.SetStateAction<number>>;
  totalCount?: number;
  setAnnotatedImageCount?: React.Dispatch<React.SetStateAction<number>>;
  annotatedImageCount?: number;
}

export interface ImageUrlModel {
  id: string;
  imageUrl: string;
  thumbUrl: string;
  annotations: any[]; // You can replace `any` with a specific type if known
  labelDetails: any[];
  projectId: string | null;
  annotationsCount: number;
  checked?: boolean;
  isAnnotated?: boolean;
  isAnnotator?: boolean;
}

export interface PayloadType {
  totalCount: number;
  annotatedImageCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  imageUrlModel: ImageUrlModel[];
}



const ClassificationImages: React.FC<ClassificationImagesProps> = ({
  gridCount,
  imagesSelection,
  setImagesSelection,
  startStop,
  setStartStop,
  selectedLbl,
  setSelectedLbl,
  taskType,
  page,
  setPage,
  count,
  setCount,
  totalPages,
  setTotalPages,
  showLabels,
  setShowLabels,
  projectId,
  setProjectId,
  currentPage,
  setCurrentPage,
  setLoader,
  loader,
  fetchImagesAccordingto,
  annotationImgs,
  setAnnotationImgs,
  setTotalCount,
  totalCount,
  setAnnotatedImageCount,
  annotatedImageCount,
}) => {
  const url = window.location.pathname;
  const segments = url.split("/");
  const lastParam = segments.pop() || "";



  const [isProcessing, setIsProcessing] = useState(false);


  const [recentlyAnnotated, setRecentlyAnnotated] = useState<
    Record<string, string>
  >({});

  const [selectedImages, setSelectedImages] = useState(new Set());
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [loadingImageId, setLoadingImageId] = useState<string | null>(null);
  // For showing labels for all images

  const [allImageLabels, setAllImageLabels] = useState<Record<string, any[]>>({});
  const [allLabelsLoading, setAllLabelsLoading] = useState(false);
  const [removingLabel, setRemovingLabel] = useState<{ imageId: string; annotationId: string } | null>(null);


  const fetchAllImageLabels = async () => {
    try {
      const res = await dispatch(
        fetchLabelsByProjectPaged({
          projectId,
          page: currentPage - 1,
          pageSize: count,
          annotation: imagesSelection,
        })
      ).unwrap();
      let imageLabels: Record<string, any[]> = {};
      if (res && Array.isArray(res.images)) {
        res.images.forEach((img: any) => {
          imageLabels[img.imageId] = img.labels || [];
        });
      }
      setAllImageLabels(imageLabels);
    } catch {
      setAllImageLabels({});
    } finally {
      setAllLabelsLoading(false);
    }
  };

  useEffect(() => {
    if (startStop == false) {
      setSelectedImages(new Set());
    }
  }, [startStop]);

  const handleImageClick = (imageId: any, e: any) => {
    if (isProcessing || !startStop) {
      return;
    }
    if (e.target.closest(".image-checkbox")) {
      return;
    }
    // Check if user has permission to annotate
    // annotationImgs is an array, so find the image object
    const imgObj = annotationImgs.find((img) => img.id === imageId);
    if (imgObj && imgObj?.isAnnotator === false) {
      toast.error("You don't have permission to annotate this image.");
      return;
    }
    toggleImageSelection(imageId);
  };

  const dispatch = useDispatch<AppDispatch>();



  const toggleImageSelection = async (imageId: string) => {
    if (isProcessing || loadingImageId) return; // Prevent clicks while processing

    setLoadingImageId(imageId); 
    setIsProcessing(true);

    try {
      dispatch(fetchImageById({ projectImageId: imageId }))
        .unwrap()
        .then(async (image: PayloadType) => {
          const imageItem = image?.imageUrlModel[0];
          // Check for duplicate labelId inside annotations array
          const hasDuplicateLabel = imageItem.annotations?.some(
            (anno: any) => anno.lableId === selectedLbl?.projectLabelId
          );

          if (hasDuplicateLabel) {
            setLoadingImageId(null);
            setIsProcessing(false);
            toast.error("This label is already annotated for this image.");
            return;
          }

          //const imageItem = annotationImgs.find((img) => img.id === imageId);
          const isAnnotated = imageItem.isAnnotated;

          if (
            taskType === "Binary Classification"
          ) {
            const lastAnnotation =
              imageItem?.annotations?.[imageItem.annotations.length - 1];

            const data: AnnotationData = {
              ...selectedLbl,
              projectImageId: imageId,
              annotationId: lastAnnotation?.annotationId || null,
            };

            let action;
            try {
              if (isAnnotated) {
                // Update existing annotation
                action = await dispatch(
                  updateAnnotation({
                    ...data,
                    annotationId: imageItem.annotations[0].annotationId,
                  })
                );
                if (action.type.endsWith("/fulfilled")) {
                  toast.success("Annotation updated successfully!");
                } else {
                  toast.error("Failed to update annotation.");
                }
              } else {
                action = await dispatch(
                  saveImageAnnotation({ ...data, annotationId: uuidv4() })
                );
                if (action.type.endsWith("/fulfilled")) {
                  toast.success("Annotation saved successfully!");
                } else {
                  toast.error("Failed to save annotation.");
                }
              }
              await dispatch(
                fetchImagesNewClass({
                  projectId: lastParam,
                  page: 1,
                  count: 1000000,
                  annotation: imagesSelection,
                })
              );
              await fetchImagesAccordingto();
            } finally {
              setIsProcessing(false);
              setLoadingImageId(null);
            }
            return;
          }

          // For Multi-Class Classification and others: always call saveImageAnnotation, don't show "Failed to update annotation"
          try {
            const action = await dispatch(
              saveImageAnnotation({
                ...selectedLbl,
                projectImageId: imageId,
                annotationId: uuidv4(),
              })
            );

            if (action.type.endsWith("/fulfilled")) {
              setRecentlyAnnotated((prev: any) => ({
                ...prev,
                [imageId]: selectedLbl?.labelColor,
              }));

              setSelectedImages((prev) => {
                const updated = new Set(prev);
                updated.delete(imageId);
                return updated;
              });

              toast.success("Annotation saved successfully!");
            } else {
              toast.error("Failed to save annotation.");
            }

            await dispatch(
              fetchImagesNewClass({
                projectId: lastParam,
                page: 1,
                count: 1000000,
                annotation: imagesSelection,
              })
            );
            await fetchImagesAccordingto();
          } finally {
            setIsProcessing(false);
            setLoadingImageId(null);
          }
        });
    } catch (error) {
      toast.error("Failed to save annotation.");
      setIsProcessing(false);
      setLoadingImageId(null);
    }
  };



  // Checkbox click handler for showing labels for all images
  const handleCheckboxClick = async () => {

    setAllLabelsLoading(true);

    fetchAllImageLabels();
  };

  useEffect(() => {
    setLoader(true);
    fetchImagesAccordingto();
  }, [dispatch, projectId, page, count, imagesSelection]);

  useEffect(() => {
    setPage(1);
    setCurrentPage(1);
  }, [imagesSelection, gridCount]);

  // Set count based on gridCount
  useEffect(() => {
    let newCount = 4;
    switch (gridCount) {
      case 2:
        newCount = 2;
        break;
      case 4:
        newCount = 4;
        break;
      case 8:
        newCount = 8;
        break;
      case 16:
        newCount = 16;
        break;
      default:
        newCount = 4;
    }
    setCount(newCount);
  }, [gridCount]);

  const handlePrevious = () => {
    if (currentPage > 2) {
      setPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages + 1) {
      setPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    handleCheckboxClick();
  }, [showLabels]);

  const fetchClassificationImages = async (page: number, count: number) => {
    try {
      const url = window.location.pathname;
      const segments = url.split("/");
      const lastParam = segments.pop() || "";
      await dispatch(
        fetchImagesNewClass({
          projectId: lastParam,
          page: 1,
          count: 1000000,
          annotation: "all",
        })
      )
        .unwrap()
        .then(() => {
          //setImagesLoaded(true);
        });
    } catch (error) {
      //toast.error("Failed to fetch image list");
    } finally {
    }
  };

  useEffect(() => {
    fetchClassificationImages(1, 10000);
  }, []);

  // Helper to get grid rows and columns based on gridCount
  const getGridDimensions = (gridCount: number, totalImages: number) => {
    switch (gridCount) {
      case 2:
        return { rows: 1, cols: 2 };
      case 4:
        return { rows: 2, cols: 2 };
      case 8:
        return { rows: 2, cols: 4 };
      case 16:
        return { rows: 4, cols: 4 };
      default:
        // fallback: try to fill as square as possible
        const cols = Math.ceil(Math.sqrt(totalImages));
        const rows = Math.ceil(totalImages / cols);
        return { rows, cols };
    }
  };

  const { rows, cols } = getGridDimensions(gridCount, annotationImgs.length);

  // Calculate grid cell size to fill the viewport exactly
  const gridCellWidth = `calc(100vw / ${cols})`;
  const gridCellHeight = `calc((100vh - 158px) / ${rows})`;

  const getGridStyles = (
    count: number
  ): { flexBasis: string; height: string } => {
    switch (count) {
      case 10:
        return {
          flexBasis: "calc(100% / 2 - 10px)",
          height: "calc(50% - 5px)",
        };
      case 20:
        return {
          flexBasis: "calc(100% / 4 - 10px)",
          height: "calc(50% - 5px)",
        };
      case 30:
        return {
          flexBasis: "calc(100% / 4 - 10px)",
          height: "calc(25% - 5px)",
        };
      case 40:
        return {
          flexBasis: "calc(100% / 8 - 10px)",
          height: "calc(24.5% - 5px)",
        };
      case 50:
        return {
          flexBasis: "calc(100% / 12 - 10px)",
          height: "calc(18% - 5px)",
        };
      default:
        return {
          flexBasis: "calc(100% / 2 - 10px)",
          height: "calc(50% - 5px)",
        };
    }
  };

  const { flexBasis, height } = getGridStyles(gridCount);

  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  useEffect(() => {
    if (startStop) {
      const handleMouseMove = (e: MouseEvent) => {
        setMousePos({ x: e.clientX, y: e.clientY });
      };
      window.addEventListener("mousemove", handleMouseMove);
      return () => window.removeEventListener("mousemove", handleMouseMove);
    }
  }, [startStop]);

  // Get all project labels from Redux
  const labels = useSelector((state: any) => state.projectLabel.labels);

  return (
    <>
      {/* Floating label name with cursor */}
      {startStop && selectedLbl?.projectLabelId && (
        <Box
          sx={{
            position: "fixed",
            left: mousePos.x + 16,
            top: mousePos.y + 16,
            pointerEvents: "none",
            zIndex: 2000,
            background: selectedLbl.labelColor || "#fff",
            color: "#222",
            borderRadius: "6px",
            padding: "4px 12px",
            fontWeight: 600,
            fontSize: "14px",
            boxShadow: "0 2px 8px #0002",
            border: "1px solid #ddd",
            opacity: 0.95,
            transition: "left 0.05s, top 0.05s",
            maxWidth: 200,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {(() => {
            // Find label name by id from Redux
            const label = labels.find((l: any) => l.id === selectedLbl.projectLabelId);
            return label ? label.name : selectedLbl.projectLabelId;
          })()}
        </Box>
      )}
      {(loader || allLabelsLoading) ? (
        <VisionStudioSpinner spinnerText={allLabelsLoading ? "Loading Labels" : "Loading Images"} />
      ) : (
        <Box>
          {annotationImgs.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                gridTemplateRows: `repeat(${rows}, 1fr)`,
                height: "calc(100vh - 158px)",
                width: "calc(100vw - 76px)",
                gap: "10px",
                boxSizing: "border-box",
                position: "absolute",
                top: 0,
                left: 0,
                background: "#fff",
                zIndex: 0,
                padding: 0,
                marginTop: 6,
                overflow: "hidden",
              }}
            >
              {annotationImgs.slice(0, rows * cols).map((item, idx) => (
                <Box
                  key={item.id}
                  onClick={(e) => loadingImageId !== item.id && handleImageClick(item.id, e)}
                  onDoubleClick={() =>
                    startStop || loadingImageId === item.id ? "" : setFullscreenImage(item.imageUrl)
                  }
                  sx={{
                    position: "relative",
                    margin: 0,
                    width: "100%",
                    height: "100%",
                    textAlign: "center",
                    background: "#8080802b",
                    borderRadius: 0,
                    transition: "all 0.3s ease-in-out",
                    cursor: startStop && !isProcessing && loadingImageId !== item.id ? "pointer" : loadingImageId === item.id ? "wait" : "default",
                    opacity: isProcessing && loadingImageId !== item.id ? 0.7 : 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    border: loadingImageId === item.id ? "2px solid #1976d2" : "1px solid #e0e0e0",
                    "&:hover .annotation-overlay": {
                      opacity: loadingImageId === item.id ? 0 : 1,
                    },
                  }}
                >
                  {/* Checkbox at top left, only if startStop is false */}

                  <img
                    src={`${process.env.REACT_APP_SERVER_BASEURL}${item.thumbUrl}`}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      boxSizing: "border-box",
                      border: !item.isAnnotator ? "2.5px dashed #1976d2" : "1.5px solid #E0E0E0",
                      boxShadow: !item.isAnnotator
                        ? "0 0 8px 2px rgba(25, 118, 210, 0.15)"
                        : "0 1px 4px 0 rgba(0,0,0,0.04)",
                      transition: "border 0.2s, box-shadow 0.2s, filter 0.3s ease",

                      pointerEvents: startStop || loadingImageId === item.id ? "none" : "auto",
                      display: "block",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      filter: loadingImageId === item.id ? "brightness(0.8)" : "none",
                    }}
                  />

                  {/* Loading overlay */}
                  {loadingImageId === item.id && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(25, 118, 210, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 5,
                        pointerEvents: "none",
                      }}
                    />
                  )}


                  {!startStop && showLabels && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        background: "rgba(0,0,0,0.5)",
                        color: "white",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 10,
                      }}
                    >
                      {allLabelsLoading ? (
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            border: "4px solid #fff",
                            borderTop: "4px solid #888",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            marginBottom: 2,
                            "@keyframes spin": {
                              from: { transform: "rotate(0deg)" },
                              to: { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      ) : Array.isArray(allImageLabels[item.id]) && allImageLabels[item.id].length > 0 ? (
                        <Box
                          sx={{
                            maxHeight: 150, // adjust based on desired limit
                            overflowY: "auto",
                            px: 1,
                          }}
                        >
                          {allImageLabels[item.id].map((annotation, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "4px",
                                flexWrap: "wrap", // wraps content if needed
                              }}
                            >
                              <Box
                                sx={{
                                  width: "14px",
                                  height: "14px",
                                  backgroundColor: annotation.labelColor,
                                  borderRadius: "50%",
                                }}
                              />
                              <Typography variant="body2">{annotation.labelName}</Typography>
                              <Button
                                size="small"
                                color="error"
                                sx={{ minWidth: 0, ml: 1, fontSize: 16, padding: "2px 6px", lineHeight: 1 }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (!annotation.annotationId || !item.id) {
                                    console.log("No annotationId or projectImageId found", annotation);
                                    return;
                                  }
                                  setRemovingLabel({ imageId: item.id, annotationId: annotation.annotationId });
                                  const deleteData = {
                                    projectImageId: item.id,
                                    annotationId: annotation.annotationId,
                                  };
                                  try {
                                    await dispatch(deleteAnnotation(deleteData)).unwrap();
                                    setAllImageLabels((prev) => {
                                      const updated = { ...prev };
                                      updated[item.id] = updated[item.id].filter((_, i) => i !== idx);
                                      return updated;
                                    });
                                    toast.success("Annotation deleted successfully!");
                                  } catch (error) {
                                    toast.error("Failed to delete annotation.");
                                  } finally {
                                    setRemovingLabel(null);
                                  }
                                }}
                              >
                                {removingLabel && removingLabel.imageId === item.id && removingLabel.annotationId === annotation.annotationId ? (
                                  <Box
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      border: "2px solid #fff",
                                      borderTop: "2px solid #888",
                                      borderRadius: "50%",
                                      animation: "spin 1s linear infinite",
                                      display: "inline-block",
                                      verticalAlign: "middle",
                                      "@keyframes spin": {
                                        from: { transform: "rotate(0deg)" },
                                        to: { transform: "rotate(360deg)" },
                                      },
                                    }}
                                  />
                                ) : (
                                  "X"
                                )}
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2">No labels</Typography>
                      )}
                    </Box>
                  )}


                  {/* Annotation overlay */}
                  <Box
                    className="annotation-overlay"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "rgba(0, 0, 0, 0.5)",
                      color: "white",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      opacity: 0,
                      transition: "opacity 0.3s ease-in-out",
                    }}
                  >
                    {item.annotations?.map((annotation, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "4px",
                        }}
                      >
                        <Box
                          sx={{
                            width: "14px",
                            height: "14px",
                            backgroundColor: annotation.labelColor,
                            borderRadius: "50%",
                          }}
                        />
                        <Typography variant="body2">
                          {annotation.projectLabel}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Only show green tick at top right for annotated images */}
                  {item.isAnnotated && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: "#4caf50",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      }}
                    >
                      <CheckIcon sx={{ color: "white", fontSize: 18 }} />
                    </Box>
                  )}

                  {/* Toggle checkbox (center) */}
                  {startStop && (
                    <Box
                      className="image-checkbox"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        backgroundColor: loadingImageId === item.id
                          ? "rgba(0, 0, 0, 0.7)"
                          : "rgba(0, 0, 0, 0.5)",
                        display:
                          loadingImageId === item.id ||
                            selectedImages.has(item.id)
                            ? "flex"
                            : "none", // Show only for clicked or selected image
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: loadingImageId === item.id ? "wait" : "pointer",
                        transition: "all 0.3s ease",
                        pointerEvents: loadingImageId === item.id ? "none" : "auto",
                        zIndex: 100,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (loadingImageId !== item.id) {
                          toggleImageSelection(item.id);
                        }
                      }}
                    >
                      {loadingImageId === item.id ? (
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            border: "4px solid white",
                            borderTop: "4px solid transparent",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                            "@keyframes spin": {
                              from: { transform: "rotate(0deg)" },
                              to: { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      ) : selectedImages.has(item.id) ? (
                        <CheckIcon
                          sx={{
                            color: "white",
                            fontSize: 30,
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh", fontSize: "24px", color: "#888" }}>
              No images available to annotate right now.
            </Typography>
          )}
          <Box>
            <Box sx={{ position: "absolute", top: "6px", left: "45%" }}>
              <Box
                id="selectImage"
                sx={{ display: "flex", gap: "10px", alignItems: "center" }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      zIndex: 1,
                      minWidth: "30px",
                      height: "30px",
                      padding: "0",
                      borderRadius: "4px",
                    }}
                    onClick={handlePrevious}
                    disabled={currentPage <= 2}
                  >
                    <NavigateBeforeOutlinedIcon />
                  </Button>

                  <div>
                    <Typography sx={{ fontSize: "14px" }}>
                      <span style={{ fontWeight: "bold" }}>
                        {currentPage - 1 > 0 ? currentPage - 1 : 1}
                      </span>{" "}
                      / {totalPages > 0 ? totalPages : 1}
                    </Typography>
                  </div>

                  <Button
                    variant="contained"
                    sx={{
                      zIndex: 1,
                      minWidth: "30px",
                      height: "30px",
                      padding: "0",
                      borderRadius: "4px",
                      color: "white",
                    }}
                    onClick={handleNext}
                    disabled={currentPage >= totalPages + 1}
                  >
                    <NavigateNextOutlinedIcon />
                  </Button>
                </div>
              </Box>
            </Box>
          </Box>

          {/* Fullscreen image viewer */}
          {fullscreenImage && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.85)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setFullscreenImage(null)}
            >
              <img
                src={`${process.env.REACT_APP_SERVER_BASEURL}${fullscreenImage}`}
                alt="Fullscreen"
                style={{
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  borderRadius: 8,
                  boxShadow: "0 4px 32px rgba(0,0,0,0.5)",
                  background: "#fff",
                }}
              />
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setFullscreenImage(null);
                }}
                sx={{
                  position: "absolute",
                  top: 24,
                  right: 24,
                  color: "white",
                  background: "rgba(0,0,0,0.5)",
                  minWidth: 0,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  fontSize: 28,
                  zIndex: 10000,
                  "&:hover": { background: "rgba(0,0,0,0.7)" },
                }}
              >
                ×
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default ClassificationImages;
